import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    user, loading, adminAccessDenied, profileError, signOut, refreshProfile,
  } = useAuth();
  const [retrying, setRetrying] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Either a true 403 from the backend, or a transient/server/parse error that
  // exhausted retries. Both leave the user without a profile — show diagnostic UI.
  if (adminAccessDenied || profileError) {
    const isForbidden = adminAccessDenied && profileError?.kind === 'forbidden';
    const title = isForbidden
      ? t('auth.adminAccessDeniedTitle')
      : t('auth.profileLoadFailedTitle');
    const body = isForbidden
      ? t('auth.adminAccessDeniedBody')
      : t('auth.profileLoadFailedBody');

    const errorDetail = profileError ? formatErrorDetail(profileError, t) : null;

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full rounded-xl border border-amber-100 bg-white p-6 shadow-sm space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-gray-900 font-medium">{title}</p>
              <p className="text-sm text-gray-600">{body}</p>
            </div>
          </div>

          {user.email && (
            <p className="text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-2 break-all">
              {t('auth.signedInAs')}: <span className="font-mono">{user.email}</span>
            </p>
          )}

          {errorDetail && (
            <details className="text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-2">
              <summary className="cursor-pointer select-none">
                {t('auth.technicalDetails')}
              </summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">{errorDetail}</pre>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            {!isForbidden && (
              <button
                type="button"
                disabled={retrying}
                onClick={async () => {
                  setRetrying(true);
                  try { await refreshProfile(); } finally { setRetrying(false); }
                }}
                className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
                {retrying ? t('auth.retrying') : t('auth.tryAgain')}
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
                await signOut();
                navigate('/login', { replace: true });
              }}
              className={`inline-flex items-center justify-center gap-2 flex-1 px-4 py-2.5 rounded-lg text-sm font-medium ${
                isForbidden
                  ? 'bg-brand-600 text-white hover:bg-brand-700'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <LogOut className="w-4 h-4" />
              {t('nav.signOut')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function formatErrorDetail(
  err: NonNullable<ReturnType<typeof useAuth>['profileError']>,
  t: (k: string) => string,
): string {
  switch (err.kind) {
    case 'forbidden':
      return `HTTP 403 · ${err.code}\n${err.message}`;
    case 'server':
      return `HTTP ${err.status} · ${err.code}\n${err.message}`;
    case 'parse':
      return `HTTP ${err.status} · ${t('auth.errParseHint')}\n${err.message}`;
    case 'network':
      return `${t('auth.errNetworkHint')}\n${err.message}`;
  }
}
